/**
 * TermsPage — Terms of Use / EULA for Prompt-Brush (web + app).
 * Linked from the app's first-launch terms gate. States the zero-tolerance
 * content policy required for App Store Guideline 1.2.
 */
import { Link } from 'react-router-dom';

const display = { fontFamily: 'Delcan Mono, monospace' } as const;
const body = { fontFamily: 'FK Grotesk Mono, monospace' } as const;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-[15px] uppercase tracking-wider text-muted-foreground mb-3" style={body}>
        {title}
      </h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-foreground/90" style={body}>
        {children}
      </div>
    </section>
  );
}

export function TermsPage() {
  return (
    <div className="min-h-screen overflow-y-auto bg-background">
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Link to="/" className="text-[13px] text-muted-foreground hover:text-foreground underline" style={body}>
          ← Prompt-Brush
        </Link>

        <h1 className="text-[34px] md:text-[42px] leading-tight text-foreground mt-8" style={display}>
          Terms of Use
        </h1>
        <p className="text-[13px] text-muted-foreground mt-3" style={body}>
          Last updated: 2026
        </p>

        <p className="text-[15px] leading-relaxed text-foreground/90 mt-8" style={body}>
          Prompt-Brush is a shared gallery of hand-drawn art. Anyone can submit a prompt, and anyone
          can draw one by hand. By using Prompt-Brush — on the web or in the app — you agree to these
          Terms of Use. If you do not agree, please do not use Prompt-Brush.
        </p>

        <Section title="Zero-tolerance content policy">
          <p>
            There is <strong>zero tolerance for objectionable content and for abusive behaviour</strong>.
            This includes, without limitation: hateful, harassing, threatening, or violent content;
            sexual content involving minors; pornography; content that promotes discrimination, bigotry,
            or harm toward any individual or group; spam; and impersonation.
          </p>
          <p>
            Content that violates this policy is <strong>removed</strong>, and the people who post it are
            <strong> removed from Prompt-Brush</strong>. We may remove content and terminate accounts at
            our discretion, without notice.
          </p>
        </Section>

        <Section title="How content is moderated">
          <p>
            Every drawing is reviewed and only appears publicly after it is approved. Submitted prompts
            are filtered before they can be drawn. We review reports and act on objectionable content
            within 24 hours by removing it and ejecting the responsible user.
          </p>
        </Section>

        <Section title="Reporting and blocking">
          <p>
            You can <strong>report</strong> any drawing for review (the “…” menu on a drawing), and you can
            <strong> block</strong> any artist (the “…” menu on a drawing, or “Block this artist” on their
            profile). Blocking removes that artist’s work from your feed immediately and notifies us so we
            can review their content. You can unblock anyone at any time.
          </p>
        </Section>

        <Section title="Your content">
          <p>
            You keep ownership of the drawings and prompts you create. By submitting them to Prompt-Brush,
            you grant us a non-exclusive, worldwide, royalty-free licence to display, reproduce, and
            distribute them within Prompt-Brush and its promotion. You are responsible for the content you
            submit and confirm you have the right to submit it.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>
            Do not use Prompt-Brush to break the law, infringe others’ rights, harass anyone, or post the
            objectionable content described above. Do not attempt to disrupt, abuse, or reverse-engineer
            the service.
          </p>
        </Section>

        <Section title="Disclaimer">
          <p>
            Prompt-Brush is provided “as is,” without warranties of any kind. To the fullest extent
            permitted by law, we are not liable for content submitted by users or for any damages arising
            from your use of the service.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or reports:{' '}
            <a href="mailto:hello@prompt-brush.com" className="underline hover:text-foreground">
              hello@prompt-brush.com
            </a>
            .
          </p>
        </Section>

        <div className="h-24" />
      </div>
    </div>
  );
}
